/**
 * PageActionLayout
 *
 * Convenience wrapper that composes the Page Action UI
 * components — PageToolbar and CommandPalette —
 * with shared state management (palette open/close).
 *
 * Wrap any page's content with this component to get the full
 * action UI layer. Pair with PageActionProvider for context.
 *
 * Usage:
 *   <PageActionProvider pageId="/entities">
 *     <PageActionLayout>
 *       <YourPageContent />
 *     </PageActionLayout>
 *   </PageActionProvider>
 */

import React, { useState, useCallback } from 'react';
import { PageToolbar } from './PageToolbar';
import { CommandPalette } from './CommandPalette';
import './PageActions.css';

export interface PageActionLayoutProps {
  children: React.ReactNode;
  /** Hide the desktop toolbar (e.g. chat page has its own controls) */
  hideToolbar?: boolean;
  /** Additional CSS class */
  className?: string;
}

export function PageActionLayout({
  children,
  hideToolbar = false,
  className,
}: PageActionLayoutProps) {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const openPalette = useCallback(() => setPaletteOpen(true), []);
  const closePalette = useCallback(() => setPaletteOpen(false), []);

  return (
    <div className={`page-action-layout ${className ?? ''}`}>
      {!hideToolbar && <PageToolbar onOverflow={openPalette} />}
      {children}
      <CommandPalette open={paletteOpen} onClose={closePalette} />
    </div>
  );
}
